/**
 * OperationsTab - Primary tab for daily dispatch operations.
 *
 * Layout: Three-column grid (left to right):
 * Col 1: Status & Task — status controls + task fields
 * Col 2: Container Information
 * Col 3: Processing Flow
 */

import React, { useEffect, useState, useRef } from 'react';
import { Select, Switch, Tooltip } from 'antd';
import {
  AlertTriangle,
  Pause,
  Play,
  ArrowRight,
  CheckCircle,
  XCircle,
  Ban,
  Zap
} from 'lucide-react';
import { TaskActionSelect } from '../panels/TaskActionSelect'
import { ContainerTask } from '../../../types/container';
import { taskStatusConfig, taskTypeConfig, taskSubtypeConfig } from '../../../data/dispatchData';
import type { Chassis } from '../../../types/chassis';
import { TaskEventsList } from '../processing/TaskEventsList';
import {
  STATUS_PHASES,
  getPhaseIndex,
  PAUSABLE_STATUSES,
  STATE_TRANSITIONS,
  getAdvanceStatus,
  getAllowedStatuses,
  dropdownCache
} from '../utils/taskConstants';
// STATUS_PHASES still used by Override menu for grouping statuses
import { chassisApi } from '../../../services/chassisApi';
import ShippingLineSelect from '../../common/ShippingLineSelect';
import { GensetPicker } from '../../shared/GensetPicker';

const LOADING_DELAY_THRESHOLD = 400

interface OperationsTabProps {
  container: ContainerTask
  onContainerChange: (updates: Partial<ContainerTask>) => void
  onPause: () => void
  onResume: () => Promise<void>
  onUpdateEta: () => void
  isResuming: boolean
  pausedPanel?: React.ReactNode
  /** DB id of the task, so Task Action can drive the real conversion. */
  taskDbId?: number | null
  /** Called after a conversion so the modal can refetch. */
  onTaskActionConverted?: () => void | Promise<void>
}

export const OperationsTab: React.FC<OperationsTabProps> = ({
  container,
  onContainerChange,
  onPause,
  onResume,
  isResuming,
  taskDbId,
  onTaskActionConverted,
  pausedPanel,
}) => {
  // State for chassis list — initialize from cache if available
  const [chassisList, setChassisList] = useState<Chassis[]>(dropdownCache.chassis || [])
  const [isLoadingChassis, setIsLoadingChassis] = useState(false)

  // Status change confirmation
  const [pendingStatus, setPendingStatus] = useState<string | null>(null)
  const [isOverride, setIsOverride] = useState(false)
  const [showOverrideMenu, setShowOverrideMenu] = useState(false)
  const overrideRef = useRef<HTMLDivElement>(null)

  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current)
    }
  }, [])

  // Close override menu on click outside
  useEffect(() => {
    if (!showOverrideMenu) return
    const handleClick = (e: MouseEvent) => {
      if (overrideRef.current && !overrideRef.current.contains(e.target as Node)) {
        setShowOverrideMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showOverrideMenu])

  // Fetch chassis on mount (use cache and delayed loading to prevent flicker)
  useEffect(() => {
    const fetchChassis = async () => {
      if (dropdownCache.chassis) {
        setChassisList(dropdownCache.chassis)
        return
      }
      if (dropdownCache.chassisPromise) {
        const data = await dropdownCache.chassisPromise
        setChassisList(data)
        return
      }
      loadingTimerRef.current = setTimeout(() => setIsLoadingChassis(true), LOADING_DELAY_THRESHOLD)
      dropdownCache.chassisPromise = (async () => {
        try {
          const response = await chassisApi.getAll({ limit: 100 })
          const data = response.data || []
          dropdownCache.chassis = data
          return data
        } catch (error) {
          console.error('Failed to fetch chassis:', error)
          return []
        }
      })()
      const data = await dropdownCache.chassisPromise
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current)
        loadingTimerRef.current = null
      }
      setIsLoadingChassis(false)
      setChassisList(data)
    }
    fetchChassis()
  }, [])

  // Style constants
  const inputClass = 'w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'
  const labelClass = 'text-xs font-medium text-gray-500 uppercase block mb-2'
  const fieldContainerClass = 'bg-gray-50 rounded-xl p-3'

  // Status Pipeline logic
  const currentStatus = container.status || ''
  const currentPhaseIdx = getPhaseIndex(currentStatus)
  const statusCfg = taskStatusConfig[currentStatus]

  const canPause = PAUSABLE_STATUSES.includes(currentStatus)
  const isPaused = currentStatus === 'paused'

  const advanceTarget = getAdvanceStatus(currentStatus, container.importExport, container.taskSubtype)
  const advanceCfg = advanceTarget ? taskStatusConfig[advanceTarget] : null
  // Statuses this task type can actually hold — the backend rejects the rest, so
  // both the "Other..." list and the Override menu are filtered through it. A Drop
  // load-out pushed into loading/empty (statuses owned by its return task) can
  // never reach completed, which strands the order without its Task 2.
  const allowedStatuses = getAllowedStatuses(container.taskType, container.taskSubtype, container.importExport)
  const isStatusAllowed = (s: string) => !allowedStatuses || allowedStatuses.has(s)
  const validNext = STATE_TRANSITIONS[currentStatus] || []
  const otherTransitions = validNext.filter(s => s !== advanceTarget && isStatusAllowed(s))

  return (
    <div className="grid grid-cols-3 gap-6">
      {/* ═══ Col 1 — Container Information + Equipment & Notes ═══ */}
      <div className="flex flex-col gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
            Container Information
          </h5>
          <div className="space-y-3">
            <div className={fieldContainerClass}>
              <label className={labelClass}>Container Number</label>
              <input
                type="text"
                value={container.containerNumber || ''}
                onChange={(e) => onContainerChange({ containerNumber: e.target.value })}
                className={`${inputClass} font-mono font-semibold`}
              />
            </div>

            <div className={fieldContainerClass}>
              <label className={labelClass}>Steamship Line</label>
              <ShippingLineSelect
                value={container.shippingLine}
                onChange={(v) => onContainerChange({ shippingLine: v })}
                placeholder="Select or type steamship line"
                style={{ width: '100%' }}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className={fieldContainerClass}>
                <label className={labelClass}>Size/Type</label>
                <Select
                  value={container.containerSize || undefined}
                  onChange={(v) => {
                    const isReefer = ['20RF', '40RF', '40RH', '40RFHC', '40RHHQ'].includes(v)
                    onContainerChange({
                      containerSize: v,
                      containerType: isReefer ? 'reefer' : 'dry'
                    })
                  }}
                  placeholder="Select size/type"
                  allowClear
                  style={{ width: '100%' }}
                >
                  <Select.OptGroup label="DRY - 20ft">
                    <Select.Option value="20GP">20GP - 20' Standard (8'6")</Select.Option>
                    <Select.Option value="20HC">20HC - 20' High Cube (9'6")</Select.Option>
                    <Select.Option value="20ST">20ST - 20' Standard (8'6")</Select.Option>
                  </Select.OptGroup>
                  <Select.OptGroup label="DRY - 40ft Standard">
                    <Select.Option value="40GP">40GP - 40' Standard (8'6")</Select.Option>
                    <Select.Option value="40DC">40DC - 40' Dry Container (8'6")</Select.Option>
                    <Select.Option value="40ST">40ST - 40' Standard (8'6")</Select.Option>
                  </Select.OptGroup>
                  <Select.OptGroup label="DRY - 40ft High Cube">
                    <Select.Option value="40HC">40HC - 40' High Cube (9'6")</Select.Option>
                    <Select.Option value="40HQ">40HQ - 40' High Cube (9'6")</Select.Option>
                  </Select.OptGroup>
                  <Select.OptGroup label="DRY - 45ft High Cube">
                    <Select.Option value="45HC">45HC - 45' High Cube (9'6")</Select.Option>
                  </Select.OptGroup>
                  <Select.OptGroup label="REEFER - 20ft">
                    <Select.Option value="20RF">20RF - 20' Reefer (8'6")</Select.Option>
                  </Select.OptGroup>
                  <Select.OptGroup label="REEFER - 40ft Standard">
                    <Select.Option value="40RF">40RF - 40' Reefer (8'6")</Select.Option>
                    <Select.Option value="40RH">40RH - 40' Reefer (8'6")</Select.Option>
                  </Select.OptGroup>
                  <Select.OptGroup label="REEFER - 40ft High Cube">
                    <Select.Option value="40RFHC">40RFHC - 40' Reefer HC (9'6")</Select.Option>
                    <Select.Option value="40RHHQ">40RHHQ - 40' Reefer HC (9'6")</Select.Option>
                  </Select.OptGroup>
                </Select>
              </div>

              <div className={fieldContainerClass}>
                <label className={labelClass}>Cargo Type</label>
                <Select
                  value={container.containerType || undefined}
                  onChange={(v) => onContainerChange({ containerType: v as 'dry' | 'reefer' })}
                  placeholder="Select cargo type"
                  style={{ width: '100%' }}
                >
                  <Select.Option value="dry">DRY</Select.Option>
                  <Select.Option value="reefer">REEFER</Select.Option>
                </Select>
              </div>
            </div>

            <div className={fieldContainerClass}>
              <div className="flex items-center justify-between mb-2">
                <label className={`${labelClass} mb-0`}>Weight</label>
                <Tooltip title={container.isOverweight ? 'Container marked as overweight — surcharge applied' : 'Mark as overweight to apply surcharge'}>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-medium uppercase ${container.isOverweight ? 'text-red-600' : 'text-gray-400'}`}>
                      OW
                    </span>
                    <Switch
                      size="small"
                      checked={container.isOverweight || false}
                      onChange={(checked) => onContainerChange({ isOverweight: checked })}
                    />
                  </div>
                </Tooltip>
              </div>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={container.weight || ''}
                  onChange={(e) => onContainerChange({ weight: e.target.value ? Number(e.target.value) : undefined })}
                  className={`${inputClass} flex-1 ${
                    container.isOverweight
                      ? 'text-red-600 border-red-300 bg-red-50'
                      : container.weight && container.weight >= 45000
                      ? 'text-red-600 border-red-300'
                      : container.weight && container.weight < 45000
                      ? 'text-green-600 border-green-300'
                      : ''
                  }`}
                  placeholder="Enter weight"
                />
                <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 flex items-center">
                  lbs
                </div>
              </div>
            </div>

            <div className={fieldContainerClass}>
              <label className={labelClass}>Seal Number</label>
              <input
                type="text"
                value={container.sealNumber || ''}
                onChange={(e) => onContainerChange({ sealNumber: e.target.value })}
                className={`${inputClass} font-mono`}
                placeholder="Enter seal number"
              />
            </div>
          </div>
        </div>

        {/* Equipment & Notes */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex-1 flex flex-col">
          <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-teal-500 rounded-full"></div>
            Equipment & Notes
          </h5>
          <div className="flex flex-col gap-3 flex-1">
            <div className={fieldContainerClass}>
              <label className={labelClass}>Chassis Number</label>
              {/* External chassis (driver-entered, no chassisId) — show read-only with override option */}
              {!container.chassisId && container.chassisNumber ? (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <span className="font-mono text-sm text-gray-900 font-medium">{container.chassisNumber}</span>
                    {container.chassisCompany && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">{container.chassisCompany}</span>
                    )}
                    <span className="text-xs text-amber-600 ml-auto">External</span>
                  </div>
                  <Select
                    value={undefined}
                    onChange={(v) => {
                      const selectedChassis = chassisList.find(c => c.chassisNumber === v)
                      onContainerChange({
                        chassisNumber: v,
                        chassisId: selectedChassis?.id,
                        chassisCompany: undefined
                      })
                    }}
                    placeholder="Override with company chassis..."
                    allowClear
                    showSearch
                    style={{ width: '100%' }}
                    size="small"
                    disabled={isLoadingChassis}
                  >
                    {chassisList.map((chassis) => (
                      <Select.Option key={chassis.id} value={chassis.chassisNumber}>
                        {chassis.chassisNumber}
                        {chassis.chassisType ? ` (${chassis.chassisType})` : ''}
                        {chassis.status && chassis.status !== 'available' ? ` [${chassis.status.toUpperCase()}]` : ''}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              ) : (
                <Select
                  value={container.chassisNumber || undefined}
                  onChange={(v) => {
                    const selectedChassis = chassisList.find(c => c.chassisNumber === v)
                    onContainerChange({
                      chassisNumber: v,
                      chassisId: selectedChassis?.id,
                      chassisCompany: undefined
                    })
                  }}
                  placeholder={isLoadingChassis ? 'Loading...' : 'Select chassis'}
                  allowClear
                  showSearch
                  style={{ width: '100%' }}
                  disabled={isLoadingChassis}
                >
                  {chassisList.map((chassis) => (
                    <Select.Option key={chassis.id} value={chassis.chassisNumber}>
                      {chassis.chassisNumber}
                      {chassis.chassisType ? ` (${chassis.chassisType})` : ''}
                      {chassis.status && chassis.status !== 'available' ? ` [${chassis.status.toUpperCase()}]` : ''}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </div>

            {/* 要哪种发电机。摆在底盘号正下面，和板子上那个标记一样贴着底盘 ——
                genset_chassis 说的就是这台底盘。
                这一项住在 orders 上，一票多柜共用一个值：在这里改，同一张订单
                的每一程都跟着变。控件和建单窗口是同一份（GensetPicker）。 */}
            <div className={fieldContainerClass}>
              <label className={labelClass}>Genset</label>
              <GensetPicker
                value={container.gensetType}
                onChange={(next) => onContainerChange({ gensetType: next })}
                idPrefix="task-genset"
              />
            </div>

            <div className={`${fieldContainerClass} flex-1 flex flex-col`}>
              <label className={labelClass}>Internal Notes</label>
              <textarea
                value={container.internalNote || ''}
                onChange={(e) => onContainerChange({ internalNote: e.target.value })}
                className={`${inputClass} resize-none flex-1 min-h-[80px]`}
                placeholder="Add internal notes..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Col 2 — Status & Task (center, high-frequency operations) ═══ */}
      <div className="space-y-4">
        {/* Status Module */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-indigo-500 rounded-full"></div>
            Status
          </h5>

          {/* Horizontal progress line */}
          <div className="flex items-center mb-4 px-2">
            {STATUS_PHASES.map((phase, idx) => {
              const isCompleted = idx < currentPhaseIdx
              const isActive = idx === currentPhaseIdx
              const isLast = idx === STATUS_PHASES.length - 1
              return (
                <React.Fragment key={phase.name}>
                  {/* Dot + label */}
                  <div className="flex flex-col items-center gap-1.5">
                    <div className={`w-5 h-5 rounded-full flex-shrink-0 transition-all border-2 ${
                      isActive
                        ? `bg-white ${phase.dotColor.replace('bg-', 'border-')} ring-4 ${phase.dotColor.replace('bg-', 'ring-')}/15`
                        : isCompleted
                        ? 'bg-emerald-100 border-emerald-300'
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      {isCompleted && (
                        <svg className="w-full h-full text-emerald-500 p-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-xs font-semibold whitespace-nowrap ${
                      isActive ? phase.activeText : isCompleted ? 'text-emerald-600' : 'text-gray-300'
                    }`}>
                      {phase.name}
                    </span>
                  </div>
                  {/* Connector line */}
                  {!isLast && (
                    <div className={`flex-1 h-[3px] mx-1.5 mt-[-20px] rounded-full ${
                      isCompleted ? 'bg-emerald-200' : 'bg-gray-100'
                    }`} />
                  )}
                </React.Fragment>
              )
            })}
          </div>

          {/* Vertical phase detail boxes */}
          <div className="flex flex-col gap-1 mb-3">
            {STATUS_PHASES.map((phase, idx) => {
              const isCompleted = idx < currentPhaseIdx
              const isActive = idx === currentPhaseIdx
              return (
                <div
                  key={phase.name}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    isActive
                      ? `${phase.activeBg} ${phase.activeText} border ${phase.activeBorder}`
                      : isCompleted
                      ? 'bg-gray-100 text-gray-500 border border-gray-200'
                      : 'bg-gray-50 text-gray-300 border border-gray-100'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    isActive ? phase.dotColor : isCompleted ? 'bg-gray-400' : 'bg-gray-200'
                  }`} />
                  {phase.name}
                </div>
              )
            })}
          </div>

          {/* Current status badge */}
          <div className="mb-3">
            <span className="text-xs font-medium text-gray-400 uppercase mr-2">Current</span>
            {statusCfg ? (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusCfg.bgColor} ${statusCfg.textColor} border ${statusCfg.borderColor}`}>
                <span className={`w-2 h-2 rounded-full ${
                  currentPhaseIdx >= 0 ? STATUS_PHASES[currentPhaseIdx].dotColor : 'bg-gray-400'
                }`} />
                {statusCfg.label}
              </span>
            ) : (
              <span className="text-sm text-gray-400">No status</span>
            )}
          </div>

          {/* Action buttons — stacked vertically */}
          <div className="space-y-2">
            {/* Advance Status button */}
            {advanceTarget && advanceCfg && (
              <button
                onClick={() => { setIsOverride(false); setPendingStatus(advanceTarget) }}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all text-sm font-semibold shadow-sm"
              >
                <ArrowRight className="w-4 h-4" />
                Advance to {advanceCfg.label}
              </button>
            )}

            {/* Terminal status indicator — show when no actions available */}
            {!advanceTarget && !canPause && !isPaused && otherTransitions.length === 0 && statusCfg && (
              <button
                disabled
                className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-default ${
                  currentStatus === 'completed'
                    ? 'bg-green-50 border border-green-200 text-green-600'
                    : currentStatus === 'cancelled'
                    ? 'bg-red-50 border border-red-200 text-red-500'
                    : 'bg-gray-50 border border-gray-200 text-gray-500'
                }`}
              >
                {currentStatus === 'completed' ? (
                  <CheckCircle className="w-4 h-4" />
                ) : currentStatus === 'cancelled' ? (
                  <Ban className="w-4 h-4" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                {statusCfg.label}
              </button>
            )}

            {/* Pause / Resume buttons */}
            <div className="flex gap-2">
              {canPause && (
                <button
                  onClick={onPause}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 hover:border-orange-400 transition-all text-sm font-medium"
                >
                  <Pause className="w-3.5 h-3.5" />
                  Pause
                </button>
              )}

              {isPaused && (
                <button
                  onClick={onResume}
                  disabled={isResuming}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-green-300 text-green-600 rounded-lg hover:bg-green-50 hover:border-green-400 transition-all text-sm font-medium disabled:opacity-50"
                >
                  <Play className="w-3.5 h-3.5" />
                  {isResuming ? 'Resuming...' : 'Resume'}
                </button>
              )}

              {/* Other transitions dropdown */}
              {otherTransitions.length > 0 && (
                <div className="flex-1">
                  <Select
                    value={undefined}
                    onChange={(v) => {
                      if (!v) return
                      setIsOverride(false)
                      setPendingStatus(v)
                    }}
                    placeholder="Other..."
                    style={{ width: '100%' }}
                  >
                    {otherTransitions.map((s) => {
                      const cfg = taskStatusConfig[s]
                      if (!cfg) return null
                      return <Select.Option key={s} value={s}>{cfg.label}</Select.Option>
                    })}
                  </Select>
                </div>
              )}

              {/* Override — jump to any status */}
              <div className="relative" ref={overrideRef}>
                <button
                  onClick={() => setShowOverrideMenu(prev => !prev)}
                  title="Jump to any status (override)"
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-all ${
                    showOverrideMenu
                      ? 'bg-amber-50 border-amber-300 text-amber-600'
                      : 'bg-white border-gray-200 text-gray-400 hover:border-amber-300 hover:text-amber-500 hover:bg-amber-50'
                  }`}
                >
                  <Zap className="w-4 h-4" />
                </button>

                {showOverrideMenu && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1 max-h-72 overflow-y-auto">
                    <div className="px-3 py-1.5 text-[10px] font-bold text-amber-600 uppercase tracking-wider border-b border-gray-100 flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      Override — Jump to
                    </div>
                    {STATUS_PHASES.map((phase) => {
                      // Keep the current status listed even if it isn't valid for this
                      // task type (legacy rows sit in one) — it's disabled, and it tells
                      // the dispatcher where the task actually is.
                      const phaseStatuses = phase.statuses.filter(s => isStatusAllowed(s) || s === currentStatus)
                      if (phaseStatuses.length === 0) return null
                      return (
                      <div key={phase.name}>
                        <div className={`px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${phase.activeText} bg-gray-50`}>
                          {phase.name}
                        </div>
                        {phaseStatuses.map((s) => {
                          const cfg = taskStatusConfig[s]
                          if (!cfg) return null
                          const isCurrent = s === currentStatus
                          return (
                            <button
                              key={s}
                              disabled={isCurrent}
                              onClick={() => {
                                setIsOverride(true)
                                setPendingStatus(s)
                                setShowOverrideMenu(false)
                              }}
                              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors ${
                                isCurrent
                                  ? 'bg-blue-50 text-blue-400 cursor-default font-semibold'
                                  : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
                              }`}
                            >
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                isCurrent ? 'bg-blue-400 ring-2 ring-blue-200' : phase.dotColor
                              }`} />
                              {cfg.label}
                              {isCurrent && <span className="ml-auto text-[10px] text-blue-400">current</span>}
                            </button>
                          )
                        })}
                      </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status Change Confirmation (inline) */}
          {pendingStatus && (() => {
            const fromCfg = taskStatusConfig[currentStatus]
            const toCfg = taskStatusConfig[pendingStatus]
            return (
              <div className={`mt-3 p-3 rounded-lg border ${
                isOverride
                  ? 'bg-amber-50 border-amber-300'
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-start gap-2">
                  {isOverride
                    ? <Zap className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    : <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  }
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 mb-1">
                      {isOverride ? 'Override — confirm status jump' : 'Confirm status change'}
                    </p>
                    {isOverride && (
                      <p className="text-[10px] text-amber-600 mb-1.5">
                        This skips the normal status flow. Use with caution.
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${fromCfg?.bgColor || 'bg-gray-100'} ${fromCfg?.textColor || 'text-gray-600'}`}>
                        {fromCfg?.label || currentStatus}
                      </span>
                      <span className="text-gray-400 text-xs">&rarr;</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${toCfg?.bgColor || 'bg-gray-100'} ${toCfg?.textColor || 'text-gray-600'}`}>
                        {toCfg?.label || pendingStatus}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setPendingStatus(null); setIsOverride(false) }}
                        className="px-3 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          onContainerChange({ status: pendingStatus })
                          setPendingStatus(null)
                          setIsOverride(false)
                        }}
                        className={`px-3 py-1 text-xs font-medium text-white rounded-lg transition-colors ${
                          isOverride
                            ? 'bg-amber-500 hover:bg-amber-600'
                            : 'bg-blue-500 hover:bg-blue-600'
                        }`}
                      >
                        {isOverride ? 'Override' : 'Confirm'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Paused Panel (conditional) */}
          {pausedPanel && <div className="mt-3 pt-3 border-t border-gray-100">{pausedPanel}</div>}
        </div>

        {/* Task Info */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-purple-500 rounded-full"></div>
            Task Info
          </h5>
          <div className="space-y-3">
            <div className={fieldContainerClass}>
              <label className={labelClass}>Task ID</label>
              <input
                type="text"
                value={container.id || ''}
                className={`${inputClass} font-mono bg-gray-100 cursor-not-allowed`}
                disabled
              />
            </div>

            <div className={fieldContainerClass}>
              <label className={labelClass}>Task Type</label>
              <Select
                value={container.taskType || undefined}
                onChange={(v) => onContainerChange({ taskType: v as ContainerTask['taskType'] })}
                placeholder="Select task type"
                allowClear
                style={{ width: '100%' }}
              >
                {Object.entries(taskTypeConfig).map(([key, config]) => (
                  <Select.Option key={key} value={key}>{config.label}</Select.Option>
                ))}
              </Select>
            </div>

            <div className={fieldContainerClass}>
              <label className={labelClass}>Task Action</label>
              {taskDbId ? (
                // Switching Drop ⇄ Live Load rebuilds the route, flips the order
                // type and reprices the driver — it needs confirmation and a
                // reason, not a silent field edit.
                <TaskActionSelect
                  taskDbId={taskDbId}
                  value={container.taskSubtype}
                  onConverted={onTaskActionConverted}
                  options={Object.entries(taskSubtypeConfig).map(([key, config]) => ({
                    value: key, label: config.label,
                  }))}
                />
              ) : (
                <Select
                  value={container.taskSubtype || undefined}
                  onChange={(v) => onContainerChange({ taskSubtype: v as 'drop' | 'live-load' })}
                  placeholder="Select action"
                  allowClear
                  style={{ width: '100%' }}
                >
                  {Object.entries(taskSubtypeConfig).map(([key, config]) => (
                    <Select.Option key={key} value={key}>{config.label}</Select.Option>
                  ))}
                </Select>
              )}
            </div>

            <div className={fieldContainerClass}>
              <label className={labelClass}>Import / Export</label>
              <Select
                value={container.importExport || 'n/a'}
                onChange={(v) => onContainerChange({ importExport: v as 'import' | 'export' | 'n/a' })}
                style={{ width: '100%' }}
              >
                <Select.Option value="import">Import</Select.Option>
                <Select.Option value="export">Export</Select.Option>
                <Select.Option value="n/a">N/A</Select.Option>
              </Select>
            </div>

            <div className={fieldContainerClass}>
              <label className={labelClass}>
                {container.importExport === 'export' ? 'Booking Number' : 'BOL (Bill of Lading)'}
              </label>
              <input
                type="text"
                value={container.importExport === 'export'
                  ? (container.bookingNumber || '')
                  : (container.bol || '')}
                onChange={(e) => onContainerChange(
                  container.importExport === 'export'
                    ? { bookingNumber: e.target.value }
                    : { bol: e.target.value }
                )}
                className={`${inputClass} font-mono`}
                placeholder={container.importExport === 'export'
                  ? 'Enter booking number'
                  : 'Enter BOL number'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Col 3 — Processing Flow ═══ */}
      <div>
        <TaskEventsList
          taskId={parseInt(container.id || container.taskId || '0')}
          taskType={container.taskType}
          showPhases={true}
          showProgress={true}
        />
      </div>
    </div>
  )
}
